// src/hooks/use-purchases.ts
import { useEffect, useState } from 'react'
import { doc, onSnapshot, Timestamp } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { useAuth } from '../providers/auth.provider'

export type UnitPurchase = {
  slug: string
  purchasedAt: Date | null
}

export type PurchaseState = {
  progressSaving: boolean
  progressSaving_at: Date | null
  unlockedAll: boolean
  unlockedAll_at: Date | null
  unlockedUnits: string[]
  unlockedUnitPurchases: UnitPurchase[]
  isUnitUnlocked: (slug: string) => boolean
  loading: boolean
}

const DEFAULTS = {
  progressSaving: false,
  progressSaving_at: null as Date | null,
  unlockedAll: false,
  unlockedAll_at: null as Date | null,
  unlockedUnits: [] as string[],
  unlockedUnitPurchases: [] as UnitPurchase[],
}

function toDate(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof Date) return value
  return null
}

export function usePurchases(): PurchaseState {
  const { user, loading: authLoading } = useAuth()
  const [state, setState] = useState({ ...DEFAULTS, loading: true })

  useEffect(() => {
    if (authLoading) return

    if (!user) {
      setState({ ...DEFAULTS, loading: false })
      return
    }

    setState(s => ({ ...s, loading: true }))

    const ref = doc(db, 'users', user.uid)
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data()
        const rawUnitPurchases: { slug: string; purchasedAt: unknown }[] = data.unlockedUnitPurchases ?? []
        setState({
          progressSaving: data.progressSaving ?? false,
          progressSaving_at: toDate(data.progressSaving_at),
          unlockedAll: data.unlockedAll ?? false,
          unlockedAll_at: toDate(data.unlockedAll_at),
          unlockedUnits: data.unlockedUnits ?? [],
          unlockedUnitPurchases: rawUnitPurchases.map(p => ({
            slug: p.slug,
            purchasedAt: toDate(p.purchasedAt),
          })),
          loading: false,
        })
      } else {
        setState({ ...DEFAULTS, loading: false })
      }
    })

    return unsubscribe
  }, [authLoading, user?.uid]) // eslint-disable-line react-hooks/exhaustive-deps

  const isUnitUnlocked = (slug: string) =>
    state.unlockedAll || state.unlockedUnits.includes(slug)

  return { ...state, isUnitUnlocked }
}
